import { div, esc, fmtDate, kr, num, pct, qty } from '../calc/format.js';
import { CFG } from '../calc/model.js';
import { el, emptyRow, headCell, sortRows } from './dom.js';
import { downloadXlsx, periodSlug } from './export.js';
import { helpButton } from './help.js';
import { state } from './state.js';

/* --------------------------------------------------------------------------
   Kampanjer och rabatt på artikel

   Kampanjer ("3 för 2") har en egen rad i exporten med kampanjens namn och
   räknas per namn. Rabatt på en enskild artikel — ett extrapris eller en
   rabatt som lämnats i kassan — ser likadan ut i exporten och redovisas
   därför tillsammans, per artikel. Fliken visas när filen har något av dem
   (state.hasPromos, state.hasDiscounts).
   -------------------------------------------------------------------------- */

/** Snittköp på övriga kvitton, de utan kampanj respektive rabatt. */
const otherAov = (s, tx, revenue) => div(s.revenue - revenue, s.tx - tx);

export function renderPromos(){
  if(!state.hasPromos && !state.hasDiscounts) return;
  const s = state.stats;
  const has = state.view.length > 0;
  const set = (id, v, m) => { el(id).textContent = v; el(id + 'M').textContent = m; };

  if(!state.hasPromos){
    set('kPromoShare', '–', 'Inga kampanjer i filen');
    set('kPromoAov', '–', '–');
  }else{
    set('kPromoShare', has ? pct(s.promoShare) : '–',
        has ? `${num(s.promoTx)} köp · ${kr(s.promoDiscount)} i rabatt` : 'Inga köp i urvalet');
    set('kPromoAov', has && s.promoTx ? kr(s.promoAov) : '–',
        has && s.promoTx && s.tx > s.promoTx ? `Övriga köp ${kr(otherAov(s, s.promoTx, s.promoRevenue))}` : '–');
  }
  if(!state.hasDiscounts){
    set('kDiscShare', '–', 'Ingen rabatt på artikel i filen');
    set('kDiscAov', '–', '–');
  }else{
    set('kDiscShare', has ? pct(s.discShare) : '–',
        has ? `${num(s.discTx)} köp · ${kr(s.articleDiscount)} i rabatt` : 'Inga köp i urvalet');
    set('kDiscAov', has && s.discTx ? kr(s.discAov) : '–',
        has && s.discTx && s.tx > s.discTx ? `Övriga köp ${kr(otherAov(s, s.discTx, s.discRevenue))}` : '–');
  }

  renderPromoTable(s, has);
  renderDiscTable(s, has);
}

function renderPromoTable(s, has){
  el('promoCard').hidden = !state.hasPromos;
  if(!state.hasPromos) return;
  const t = el('tPromos');
  if(!has || !s.promos.size){
    t.innerHTML = emptyRow(5, 'Inga kampanjer i urvalet', 'Välj en längre period eller ett bredare urval.');
    return;
  }
  const rows = [...s.promos.values()].sort((a, b) => b.discount - a.discount || a.name.localeCompare(b.name, 'sv'));
  let html = `<thead><tr><th>Kampanj</th><th class="num">Tillfällen</th><th class="num">Köp</th>
    <th class="num">Rabatt</th><th class="num" title="Andel av all kampanjrabatt i perioden">Andel</th></tr></thead><tbody>`;
  for(const p of rows){
    html += `<tr>
      <td class="name">${esc(p.name)}</td>
      <td class="num">${esc(num(p.times))}</td>
      <td class="num">${esc(num(p.receipts))}</td>
      <td class="num">${esc(kr(p.discount))}</td>
      <td class="num">${esc(pct(div(p.discount, s.promoDiscount) * 100))}</td>
    </tr>`;
  }
  el('tPromos').innerHTML = html + '</tbody>';
}

/** Rabattabellens rader: en per artikel som sålts med rabatt. */
function discRows(s){
  return [...s.discArticles.values()].map(d => ({
    ...d, rate: div(d.discount, d.gross) * 100
  }));
}

function renderDiscTable(s, has){
  el('discCard').hidden = !state.hasDiscounts;
  if(!state.hasDiscounts) return;
  el('discHelp').innerHTML = helpButton('rabatt');
  const t = el('tDisc');
  el('btnExportDisc').disabled = !(has && s.discArticles.size);
  if(!has || !s.discArticles.size){
    el('cDisc').textContent = '';
    t.innerHTML = emptyRow(6, 'Ingen rabatt på artikel i urvalet', 'Välj en längre period eller ett bredare urval.');
    return;
  }
  const all = discRows(s);
  const rows = sortRows(all, state.discSort).slice(0, CFG.MAX_ROWS);
  el('cDisc').textContent = all.length > CFG.MAX_ROWS
    ? `Visar ${num(CFG.MAX_ROWS)} av ${num(all.length)} artiklar`
    : `${num(all.length)} ${all.length === 1 ? 'artikel' : 'artiklar'}`;

  const S = state.discSort;
  let html = '<thead><tr>' +
    headCell('Artikel', 'name', S) +
    headCell('Gånger', 'rows', S, 'num') +
    headCell('Antal', 'units', S, 'num') +
    headCell('Rabatt', 'discount', S, 'num') +
    headCell('Rabattsats', 'rate', S, 'num') +
    headCell('Period', 'first', S) +
    '</tr></thead><tbody>';
  for(const r of rows){
    html += `<tr>
      <td class="name">${esc(r.name)}${r.articleNumber && r.articleNumber !== r.name ? `<small>${esc(r.articleNumber)}</small>` : ''}</td>
      <td class="num">${esc(num(r.rows))}</td>
      <td class="num">${esc(qty(r.units))}</td>
      <td class="num">${esc(kr(r.discount))}</td>
      <td class="num">${r.gross > 0 ? esc(pct(r.rate)) : '–'}</td>
      <td class="nowrap">${esc(r.first === r.last ? fmtDate(r.first) : `${fmtDate(r.first)} – ${fmtDate(r.last)}`)}</td>
    </tr>`;
  }
  t.innerHTML = html + '</tbody>';
}

export function exportDiscounts(){
  const rows = discRows(state.stats)
    .sort((a, b) => b.discount - a.discount)
    .map(d => [d.articleNumber, d.name, d.rows, d.units, d.discount,
               d.gross, d.gross > 0 ? d.rate : null, d.first, d.last]);
  return downloadXlsx(`easycashier-rabatt-per-artikel-${periodSlug()}`, {
    sheet: 'Rabatt på artikel',
    columns: [{h:'Artikelnummer'}, {h:'Benämning'}, {h:'Gånger', t:'int'}, {h:'Antal', t:'num'},
      {h:'Rabatt inkl. moms', t:'num'}, {h:'Pris före rabatt inkl. moms', t:'num'}, {h:'Rabattsats %', t:'pct'},
      {h:'Första dag', t:'date'}, {h:'Sista dag', t:'date'}],
    rows
  });
}
